import React, { useRef, useState, useEffect, useContext, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import html2pdf from "html2pdf.js";
import { UserContext, ModalContext, useEntitlements } from "../../contexts";
import { useStoreWriteGuard } from "../../helpers/storeScope";
import { ListingList } from "../../components/listing";
import { PageLayout, PageHeader } from "../../components/layout";
import {
  Button,
  IconButton,
  Modal,
  PageFilterBar,
  ScopeSelect,
  ScrollToTop,
  SearchInput,
  StatePanel,
} from "../../components/ui";
import useBreakpoint from "../../hooks/useBreakpoint";
import { useListingList, ListingListProvider } from "../useListingList";
import { useGbpToBase } from "../../core/currency";
import PriceReviewBanner from "../../rc/PriceReviewBanner";
import RCListingCard from "../components/RCListingCard";
import ListingDictation from "../components/ListingDictation";
import { LiveLookbookLink } from "../components/lookbook/LiveLookbookLink";
import RCFilters from "../../views/Search/RCFilters";
import {
  convertShopFiltersToManageListings,
  convertManageListingsToShopFilters,
} from "../molecules/ListingSearch";
import { LISTINGS_TABS, ListingsTabIcon } from "../constants/listingsTabs.jsx";
import "./rc-listings-page.scss";
import { HOME_PATH } from "../../core/config/dashboard.js";

// The three scope flags, in the order they read in the trigger label. Each one
// is independent — any combination is valid, and none on means "everything".
// Longest label first, shortest last — the ragged edge falls to the right, so
// the ticked boxes stay on one straight line down the left of the panel.
const SCOPES = [
  { key: "consignment", label: "Consignment" },
  { key: "inStock", label: "In stock" },
  { key: "sold", label: "Sold" },
];

const INSURANCE_PANEL_URL = "https://insurance.archgroup.com/coverhub/";

const openInsurancePanel = () => {
  window.open(INSURANCE_PANEL_URL, "_blank", "noopener,noreferrer");
};

const RCListingsInner = () => {
  const { user } = useContext(UserContext);
  const { setSearchShown } = useContext(ModalContext);
  const [liveLookbookOpen, setLiveLookbookOpen] = useState(false);
  const { pathname, search } = useLocation();
  const history = useHistory();
  // Price review brand ("all" = every flagged brand). Seeded from
  // ?priceReview=<brand> so a banner click on /rc/overview lands here already
  // filtered; kept in the URL so reload / back keep the review open.
  // ?priceReviewReason= picks the banner button: "price" or "wf_payout"
  // (none = both reasons listed).
  const [selectedBrand, setSelectedBrandState] = useState(
    () => new URLSearchParams(search).get("priceReview") || null
  );
  const [selectedReason, setSelectedReason] = useState(
    () => new URLSearchParams(search).get("priceReviewReason") || null
  );
  const setSelectedBrand = (brand, reason = null) => {
    setSelectedBrandState(brand);
    setSelectedReason(brand ? reason : null);
    const params = new URLSearchParams(search);
    if (brand) params.set("priceReview", brand);
    else params.delete("priceReview");
    if (brand && reason) params.set("priceReviewReason", reason);
    else params.delete("priceReviewReason");
    const qs = params.toString();
    history.replace(`${pathname}${qs ? `?${qs}` : ""}`);
  };
  const [searchVal, setSearchVal] = useState("");
  const [filtersShown, setFiltersShown] = useState(false);
  const printRef = useRef(null);

  const {
    count,
    listParams,
    onSearchQueryChange,
    filter,
    onAdvancedFiltersChange,
    inStock,
    setInStock,
    isConsignment,
    setConsignment,
    sold,
    setSold,
    showsStockValue,
    stockValue,
  } = useListingList();

  // The total arrives as a GBP figure (summed in SQL off the GBP pivots), so it
  // goes through useGbpToBase — which converts it to the store's base currency,
  // or keeps labelling it GBP when the rate is unavailable rather than stamping
  // the base symbol on an unconverted number.
  const gbpToBase = useGbpToBase();

  useEffect(() => {
    setSearchShown(filtersShown);
  }, [filtersShown, setSearchShown]);

  const handleSearch = (e) => {
    setSearchVal(e.target.value);
    onSearchQueryChange(e.target.value);
  };

  // Three independent flags behind one control. The trigger names whatever is
  // on, so the row costs one field instead of three switches and the current
  // scope is still readable without opening anything.
  const scopeState = { inStock: !!inStock, consignment: !!isConsignment, sold: !!sold };
  const setScope = { inStock: setInStock, consignment: setConsignment, sold: setSold };

  const displayFilters = convertManageListingsToShopFilters(filter);
  const filterCount = Object.keys(displayFilters).filter((k) => k !== "category_id").length;

  const onFiltersApplied = (searchData) => {
    setFiltersShown(false);
    const converted = convertShopFiltersToManageListings(searchData.filters);
    onAdvancedFiltersChange(converted);
  };

  const onDownload = () => {
    const element = printRef.current || document.body;
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    html2pdf()
      .from(element)
      .set({
        margin: 0.5,
        filename: `RCListings_${timestamp}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      })
      .save();
  };

  const activeTabPath =
    LISTINGS_TABS.find((t) => pathname === t.path || pathname.startsWith(t.path + "/"))
      ?.path ?? LISTINGS_TABS[0].path;

  // Tabs follow the plan: Lookbooks (Feature 13) and Listing Network
  // (Feature 22, Future Release) each disappear when not included.
  const { hasFeature } = useEntitlements();
  const hasLookbooks = hasFeature("lookbook_generation");

  const tabs = useMemo(
    () =>
      LISTINGS_TABS.filter((tab) => !tab.feature || hasFeature(tab.feature))
        .filter((tab) => hasLookbooks || tab.path !== "/listings/lookbooks")
        .map((tab) => ({
          label: tab.label,
          to: tab.path,
          icon: <ListingsTabIcon src={tab.iconSrc} />,
        })),
    [hasLookbooks, hasFeature]
  );

  // Creating from "Master Store" has no single store to save into — the
  // backend would file it against the RC master store. See useStoreWriteGuard.
  const { guardProps: storeGuard } = useStoreWriteGuard();
  // 900px — the line where the app swaps to its mobile frame (bottom tab bar).
  const { isAtLeastMd } = useBreakpoint();

  const actions = useMemo(
    () => [
      // The mic leads the row, in front of "+ New Stock": dictation is just
      // another way to start a listing, so it belongs with the create actions
      // rather than tacked onto the search pill. Gated by the same store write
      // guard as the buttons — dictation ends at the same create form.
      <ListingDictation key="dictate" disabled={!!storeGuard.disabled} title={storeGuard.title} />,
      // "+ New Listing" (→ /product/create) hidden per Alex — the New Stock /
      // New Consignment flows supersede it on the rc/listings page.
      { label: "+ New Stock", to: "/product/create/stock", variant: "primary", size: "lg", className: "rc-ui-btn--hero", "data-btn": "listing-new-stock", ...storeGuard },
      { label: "+ New Consignment", to: "/product/create/consignment", variant: "primary", size: "lg", className: "rc-ui-btn--hero", "data-btn": "listing-new-consignment", ...storeGuard },
      // Lookbook is the quieter of the three — secondary, sat on the right.
      ...(hasLookbooks
        ? [{ label: "+ New Lookbook", to: "/listings/lookbooks", variant: "secondary", size: "lg", className: "rc-ui-btn--hero", "data-btn": "lookbook-new", ...storeGuard }]
        : []),
      // The store's live lookbook link (admin-only API). Disabled on Master
      // Store — there is no single store whose stock it would show.
      ...(hasLookbooks && user?.is_admin
        ? [{ label: "Live Lookbook", onClick: () => setLiveLookbookOpen(true), variant: "secondary", size: "lg", className: "rc-ui-btn--hero", "data-btn": "live-lookbook", ...storeGuard }]
        : []),
    ],
    [storeGuard, hasLookbooks, user?.is_admin]
  );

  // F5: a store with no listings at all sees one friendly empty state; any search,
  // advanced filter or scope flag (In stock / Consignment / Sold) gets "No matches"
  // instead. "Add a watch" wears the same storeGuard as "+ New Stock"; "Import stock"
  // only shows to admins because /stock/import is a requireAdmin route.
  const isUnfiltered = !searchVal && filterCount === 0 && !inStock && !isConsignment && !sold;
  const listingsEmpty = isUnfiltered ? (
    <StatePanel
      state="empty"
      title="No listings yet"
      description="Add your first watch, or import your stock in one go."
      action={{ label: "Add a watch", to: "/product/create/stock", ...storeGuard }}
      secondaryAction={user?.is_admin ? { label: "Import stock", to: "/stock/import", ...storeGuard } : undefined}
    />
  ) : (
    <StatePanel state="no-results" compact />
  );

  return (
    <PageLayout className="rc-listings" ref={printRef}>
      <PageHeader
        title="Manage Listings"
        description="View, edit, and organize all your active and archived listings in one place."
        backTo={HOME_PATH}
        // Sticky only where there is room for it. On a phone the header, its tabs
        // and the action row ate a third of the screen and never gave it back,
        // so below 900px the whole top scrolls away and ScrollToTop brings it
        // back in one tap. PageHeader skips its compact-on-scroll observer
        // entirely when it is not sticky, so the title stops resizing too.
        sticky={isAtLeastMd}
        tabs={tabs}
        actions={actions}
        activePath={activeTabPath}
      />

      <PriceReviewBanner
        onBrandSelect={setSelectedBrand}
        selectedBrand={selectedBrand}
        selectedReason={selectedReason}
      />

      {/* Shared row — same geometry as Buy Alerts (PageFilterBar, M54D). */}
      <PageFilterBar
        className="rc-listings__filterbar"
        label="Filter listings"
        search={(
          <SearchInput
            size="lg"
            value={searchVal}
            onChange={handleSearch}
            placeholder="Quick search for products ..."
          />
        )}
      >
          <ScopeSelect
            label="Listing scope"
            placeholder="All listings"
            options={SCOPES.map((s) => ({ key: s.key, label: s.label }))}
            value={SCOPES.filter((s) => scopeState[s.key]).map((s) => s.key)}
            onChange={(next) => SCOPES.forEach((s) => {
              const on = next.includes(s.key);
              if (on !== scopeState[s.key]) setScope[s.key](on);
            })}
          />

          <Button
            type="button"
            variant={filterCount > 0 ? "secondary" : "ghost"}
            size="lg"
            onClick={() => setFiltersShown(!filtersShown)}
            leadingIcon={
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <line x1="2" y1="5" x2="14" y2="5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                <line x1="4" y1="9" x2="12" y2="9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                <line x1="6" y1="13" x2="10" y2="13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            }
          >
            {`Filter${filterCount > 0 ? ` (${filterCount})` : ""}`}
          </Button>

          <RCFilters
            active={filtersShown}
            search={{ query: searchVal, list: "uploaded", filters: displayFilters }}
            onApply={onFiltersApplied}
            close={() => setFiltersShown(false)}
            includeStatusFilter={true}
            includeOwnedByFilter={true}
          />

          {/* No count — this button has no behaviour wired up yet. */}
          <Button
            type="button"
            variant="ghost"
            size="lg"
            leadingIcon={
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M8 2.5L14 13.5H2L8 2.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                <path d="M8 7v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
              </svg>
            }
          >
            Alerts
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="lg"
            className="rc-listings__insurance-btn"
            onClick={openInsurancePanel}
            leadingIcon={
              <img
                src="/assets/icons/insurance-panel-shield.png"
                alt=""
                className="rc-listings__insurance-icon"
              />
            }
          >
            Insurance
          </Button>
      </PageFilterBar>

      <div className="rc-listings__count-row">
        <span className="rc-listings__count">
          {selectedBrand
            ? <>Showing {selectedReason === "wf_payout" ? "WF payout below store" : "price review"} listings for <strong>{selectedBrand === "all" ? "all brands" : selectedBrand === "others" ? "other brands" : selectedBrand}</strong></>
            : (
              <>
                {count != null ? count : "—"} {count === 1 ? "listing" : "listings"}
                {/* Value of exactly these listings — same scope, same filters.
                    Only offered alongside the stock pills (see showsStockValue). */}
                {showsStockValue && (
                  <>
                    <span className="rc-listings__count-sep" aria-hidden="true">·</span>
                    <strong className="rc-listings__count-value">
                      {stockValue != null
                        ? gbpToBase.format(stockValue, { decimals: 0 })
                        : "—"}
                    </strong>
                    {" "}total value
                  </>
                )}
              </>
            )}
        </span>
        <div className="rc-listings__count-actions">
          {(inStock || isConsignment) && (
            <IconButton
              size="xl"
              aria-label="Print and download listings"
              title="Print and download"
              onClick={onDownload}
              className="rc-listings__download-btn"
            >
              <img src="/assets/download_icon.png" alt="" style={{ width: 17, height: 20 }} />
            </IconButton>
          )}
        </div>
      </div>

      <div className="rc-listings__grid">
        {selectedBrand ? (
          <ListingList
            key={`brand-${selectedBrand}-${selectedReason || "all"}`}
            itemComponent={RCListingCard}
            itemComponentProps={{ priceReviewMode: true }}
            limit={24}
            url={`/api/listings/competitive-analysis/missing/${encodeURIComponent(selectedBrand)}${selectedReason ? `?reason=${encodeURIComponent(selectedReason)}` : ""}`}
            {...listParams}
          />
        ) : (
          <ListingList
            itemComponent={RCListingCard}
            limit={24}
            list="uploaded"
            empty={listingsEmpty}
            {...listParams}
          />
        )}
      </div>

      {/* The list runs to hundreds of cards and the header no longer follows
          you down on a phone, so this is the way back to the filters. */}
      <ScrollToTop />

      <Modal
        open={liveLookbookOpen}
        onClose={() => setLiveLookbookOpen(false)}
        title="Live Lookbook"
        description="One link that always shows your active listings."
      >
        {liveLookbookOpen && <LiveLookbookLink />}
      </Modal>
    </PageLayout>
  );
};

const RCListingsPage = () => (
  <ListingListProvider key="rc-listings">
    <RCListingsInner />
  </ListingListProvider>
);

export default RCListingsPage;
